import React, { useState } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Webhook, Eye, EyeOff, Copy, CheckCircle } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const SANDBOX_KEYS = {
  api_key: 'sk_sandbox_payzen_xxxxxxxxxxxxxxxxxx',
  webhook_secret: 'whsec_sandbox_xxxxxxxxxxxxxxxx',
  merchant_id: 'mid_sandbox_payzen_demo',
};
const LIVE_KEYS = {
  api_key: 'sk_live_payzen_xxxxxxxxxxxxxxxxxx',
  webhook_secret: 'whsec_live_xxxxxxxxxxxxxxxx',
  merchant_id: 'mid_live_payzen_prod',
};

export default function BackOfficeConfigIntegration() {
  const [showKeys, setShowKeys] = useState({});
  const [copied, setCopied] = useState(null);

  const toggleShow = (key) => setShowKeys((p) => ({ ...p, [key]: !p[key] }));
  const copyVal = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    toast.success('Copied');
    setTimeout(() => setCopied(null), 2000);
  };

  const renderKeyRow = (label, keyName, value, env) => {
    const fullKey = `${env}-${keyName}`;
    return (
      <div key={fullKey} className="flex items-center justify-between py-3 border-b border-[#DCE4E0]/60 last:border-0">
        <div className="min-w-0 flex-1 mr-4">
          <Label className="text-xs text-slate-500">{label}</Label>
          <div className="flex items-center gap-2 mt-1">
            <code className="text-sm font-mono bg-[#F8FAF9] px-2 py-1 rounded border border-[#DCE4E0] truncate max-w-[320px]">
              {showKeys[fullKey] ? value : '•'.repeat(32)}
            </code>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" onClick={() => toggleShow(fullKey)} className="text-slate-500 hover:text-slate-800 hover:bg-slate-100">
            {showKeys[fullKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => copyVal(value, fullKey)} className="text-slate-500 hover:text-slate-800 hover:bg-slate-100">
            {copied === fullKey ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="pb-4">
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><Webhook className="w-7 h-7 text-[#0B6445]" />Integration Settings</h1>
          <p className="text-[#0E1B16]/70 mt-2">API keys, webhook secrets, and external integration credentials.</p>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base text-[#073B2E]">Sandbox Environment</CardTitle>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Test</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {renderKeyRow('API Key', 'api_key', SANDBOX_KEYS.api_key, 'sandbox')}
            {renderKeyRow('Webhook Secret', 'webhook_secret', SANDBOX_KEYS.webhook_secret, 'sandbox')}
            {renderKeyRow('Merchant ID', 'merchant_id', SANDBOX_KEYS.merchant_id, 'sandbox')}
          </CardContent>
        </Card>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base text-[#073B2E]">Live Environment</CardTitle>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Production</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {renderKeyRow('API Key', 'api_key', LIVE_KEYS.api_key, 'live')}
            {renderKeyRow('Webhook Secret', 'webhook_secret', LIVE_KEYS.webhook_secret, 'live')}
            {renderKeyRow('Merchant ID', 'merchant_id', LIVE_KEYS.merchant_id, 'live')}
          </CardContent>
        </Card>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base text-[#073B2E]">Webhook Endpoints</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { event: 'payment.completed', url: 'https://api.payzen.com.au/webhooks/payment-completed', active: true },
                { event: 'payment.failed', url: 'https://api.payzen.com.au/webhooks/payment-failed', active: true },
                { event: 'kyc.status_changed', url: 'https://api.payzen.com.au/webhooks/kyc-status', active: true },
                { event: 'account.created', url: 'https://api.payzen.com.au/webhooks/account-created', active: false },
              ].map((wh) => (
                <div key={wh.event} className="flex items-center justify-between py-2 border-b border-[#DCE4E0]/60 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{wh.event}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{wh.url}</p>
                  </div>
                  <Badge variant="outline" className={wh.active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-500 border-slate-200'}>{wh.active ? 'Active' : 'Disabled'}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
}
